import { Component, OnInit, ViewChild, TemplateRef, OnDestroy, effect, input, output, signal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { ImportType } from 'src/app/models/import-type';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { IntegrationRunTimeService } from 'src/app/services/integration-runtime.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';


import { Integration } from '../../models/integration';
import { IntegrationRunTime } from '../../models/integration-runtime';
import { User } from '../../models/user';
import { ToastService } from '../../services/toast.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-integration-runtime',
    templateUrl: './integration-runtime.component.html',
    styleUrls: ['./integration-runtime.component.scss'],
    standalone: false,
})
export class IntegrationRunTimeComponent implements OnInit, OnDestroy {

    constructor(
        private clientService: ClientIntegrationService,
        private integrationRunTimeService: IntegrationRunTimeService,
        private typeConstantService: TypeConstantService,
        private toastService: ToastService,
        private user: User,
        private dialog: MatDialog,
        private router: Router,
    ) {
        effect(() => {
            this.integration(); // track the dependency
            untracked(() => this.loadRunTimes());
        });
    }

    private sub: any;
    integration = input.required<Integration>();
    destinationEndpoints = input.required<DestinationEndpoint[]>();
    runTimesPopulated = output<boolean>();
    componentDestroyed$ = new Subject<boolean>();
    integrationRunTimes: IntegrationRunTime[];
    integrationRunTime: IntegrationRunTime;
    importTypes: ImportType[];
    cacheImportTypes: number[] = [];
    noDestinationEndpointId: number;
    selectedImportTypes: ImportType[] = [];
    loadingRunTimes = signal(false);
    integrationRunTimeSelected = false;
    destinationsAndImportTypes: string[] = [];
    addingIntegrationRunTime = false;
    lastRunTimeDateInput: Date;
    nextRunTimeDateInput: Date;
    savingIntegrationRuntime = false;
    @ViewChild('deleteRunTimeDialog', { static: true }) deleteRunTimeDialog: TemplateRef<any>;

    ngOnInit(): void {
        this.integrationRunTimeSelected = false;

        const today = new Date();
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);

        this.lastRunTimeDateInput = new Date(today);

        today.setSeconds(1);

        this.nextRunTimeDateInput = new Date(today);

        for (const endpoint of this.destinationEndpoints()) {
            if (endpoint.endpointName == 'NoDestination') {
                this.noDestinationEndpointId = endpoint.destinationEndpointId;
            }
        }

        this.typeConstantService.getAllImportTypes()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: (value) => {
                    this.importTypes = value;
                    for (const importType of this.importTypes) {
                        if (importType.typeDescription == 'EmployeeCache' || importType.typeDescription == 'BranchCache') {
                            importType.typeDescription += '-(NoDestination)';
                            this.cacheImportTypes.push(importType.importTypeId);
                        }
                    }
                },
                error: (_error) => {
                    this.savingIntegrationRuntime = false;
                    this.toastService.toastCreate('Failed to get Import Types.', 'Warning', {
                        autoClose: false,
                    });
                },
            });
    }

    private loadRunTimes(): void {
        this.loadingRunTimes.set(true);
        this.integrationRunTimeService.getAllIntegrationRunTimesByIntegration(this.integration().integrationId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: (value) => {
                    // on success
                    this.integrationRunTimes = value;
                    this.loadingRunTimes.set(false);

                    if (this.integrationRunTimes?.length > 0) {
                        this.integrationRunTimes = this.integrationRunTimes.sort((a, b) => { return a.importTypeId - b.importTypeId; });
                        this.selectIntegrationRunTime(this.integrationRunTimes[0]);
                        this.integrationRunTimeSelected = true;
                        this.runTimesPopulated.emit(true);
                    }
                },
                error: (_error) => {
                    this.loadingRunTimes.set(false);
                    this.savingIntegrationRuntime = false;
                    this.toastService.toastCreate('Failed to get Integration Run Times.', 'Warning', {
                        autoClose: false,
                    });
                },
            });
    }

    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    updateIntegrationRunTime(num?: number, timeType?: string): void {

        this.savingIntegrationRuntime = true;

        if ((num != null) && (timeType != null)) {
            if (timeType == 'hours') {
                const currentDate = new Date(this.integrationRunTime.lastRunTime);
                this.lastRunTimeDateInput = new Date(
                    (currentDate.getTime() - (num * 60 * 60 * 1000)),
                );

                this.nextRunTimeDateInput = new Date(this.integrationRunTime.nextRunTime);
            }

        }
        //Else use lastruntime and nextruntime, already set

        if (this.lastRunTimeDateInput.getTime() >= this.nextRunTimeDateInput.getTime()) {
            this.toastService.toastCreate('Invalid date/time: lastRunTime is greater than the nextRunTime.', 'Warning', {
                autoClose: false,
            });

            this.savingIntegrationRuntime = false;
        } else {
            const lrtUTC = this.lastRunTimeDateInput.toUTCString();
            const nrtUTC = this.nextRunTimeDateInput.toUTCString();
            this.integrationRunTime.lastRunTime = new Date(lrtUTC);
            this.integrationRunTime.nextRunTime = new Date(nrtUTC);
            this.integrationRunTimeService.updateIntegrationRunTime(this.integrationRunTime)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe({
                    next: (_value) => {
                        // on success
                        this.toastService.toastCreate('Successfully updated the runtime for ' + this.integration().name + ': '
                            + this.integrationRunTime.importTypeDescription + ' ' + this.integrationRunTime.destinationEndpointName + '.', 'Success', {
                            autoClose: false,
                        });

                        this.savingIntegrationRuntime = false;
                    },
                    error: (_error) => {
                        this.savingIntegrationRuntime = false;
                        this.toastService.toastCreate('Failed to update the runtime for ' + this.integration().name + ': '
                            + this.integrationRunTime.importTypeDescription + ' ' + this.integrationRunTime.destinationEndpointName + '.', 'Warning', {
                            autoClose: false,
                        });
                    },
                });
        }
    }

    rollbackIntegrationRuntime(rollback: string): void {

        let today;

        if (rollback == 'StartOfMonth') {
            today = new Date();
            today = new Date(today.getFullYear(), today.getMonth());
            today.setHours(0);
            today.setMinutes(0);
            today.setSeconds(0);
            today.setMilliseconds(0);
        } else if (rollback == 'StartOfDay') {
            today = new Date();
            today.setHours(0);
            today.setMinutes(0);
            today.setSeconds(0);
            today.setMilliseconds(0);
        }

        this.lastRunTimeDateInput = today;

        this.updateIntegrationRunTime();
    }

    addIntegrationRunTime(): void {

        this.savingIntegrationRuntime = true;

        //Last runtime and next runtime are already set

        if (this.lastRunTimeDateInput.getTime() >= this.nextRunTimeDateInput.getTime()) {
            this.toastService.toastCreate('Invalid date/time: lastRunTime is greater than the nextRunTime.', 'Warning', {
                autoClose: false,
            });

            this.savingIntegrationRuntime = false;
        }
        else if (this.integrationRunTime.destinationEndpointId == -1) {
            this.toastService.toastCreate('No DestinationEndpoint Selected', 'Warning', {
                autoClose: false,
            });

            this.savingIntegrationRuntime = false;
        }
        else if (this.selectedImportTypes.length < 1) {
            this.toastService.toastCreate('No ImportType Selected', 'Warning', {
                autoClose: false,
            });

            this.savingIntegrationRuntime = false;
        }
        //dE.destinationEndpointId + " " + iT.importTypeId
        else {
            let runTimesToAdd = [];
            for (const importType of this.selectedImportTypes) {
                if (this.destinationsAndImportTypes.indexOf(this.integrationRunTime.destinationEndpointId + ' ' + importType.importTypeId) > -1) {
                    // console.log(this.destinationsAndImportTypes);
                    this.toastService.toastCreate(this.integrationRunTime.destinationEndpointName + ' ' + importType.typeDescription + ' RunTime already exists.', 'Warning', {
                        autoClose: false,
                    });

                    this.savingIntegrationRuntime = false;
                    return;
                }
                const newRunTime = new IntegrationRunTime();
                newRunTime.lastRunTime = this.lastRunTimeDateInput;
                newRunTime.nextRunTime = this.nextRunTimeDateInput;
                newRunTime.integrationId = this.integrationRunTime.integrationId;
                newRunTime.importTypeId = importType.importTypeId;
                newRunTime.importTypeDescription = importType.typeDescription.split('-')[0];
                //If cache then NoDestination
                if (this.cacheImportTypes.indexOf(newRunTime.importTypeId) != -1) {
                    //Check for duplicate cache
                    if (this.destinationsAndImportTypes.indexOf(this.noDestinationEndpointId + ' ' + importType.importTypeId) > -1) {
                        this.toastService.toastCreate('NoDestination' + ' ' + importType.typeDescription + ' RunTime already exists.', 'Warning', {
                            autoClose: false,
                        });

                        this.savingIntegrationRuntime = false;
                        return;
                    }

                    newRunTime.destinationEndpointId = this.noDestinationEndpointId;
                    newRunTime.destinationEndpointName = 'NoDestination';
                }
                else {
                    newRunTime.destinationEndpointId = this.integrationRunTime.destinationEndpointId;
                    newRunTime.destinationEndpointName = this.integrationRunTime.destinationEndpointName;
                }
                runTimesToAdd.push(newRunTime);
            }

            this.integrationRunTimeService.addIntegrationRunTimes(runTimesToAdd)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe({
                    next: (value) => {
                        runTimesToAdd = value;
                        for (const runTime of runTimesToAdd) {
                            this.integrationRunTimes.push(runTime);
                        }

                        // on success
                        this.toastService.toastCreate('Successfully added the runtime for ' + this.integration().name + ': ' + this.integrationRunTime.importTypeDescription + ' ' + this.integrationRunTime.destinationEndpointName + '.', 'Success', {
                            autoClose: false,
                        });
                        this.addingIntegrationRunTime = false;

                        this.selectedImportTypes = [];
                        this.integrationRunTime = runTimesToAdd[0];
                        this.selectIntegrationRunTime(this.integrationRunTime);

                        if (this.integrationRunTime != undefined) {
                            this.integrationRunTimeSelected = true;
                        }

                        this.savingIntegrationRuntime = false;
                    },
                    error: (_error) => {
                        this.savingIntegrationRuntime = false;
                        this.toastService.toastCreate('Failed to add the runtime for ' + this.integration().name + ': ' + this.integrationRunTime.importTypeDescription + ' ' + this.integrationRunTime.destinationEndpointName + '.', 'Warning', {
                            autoClose: false,
                        });
                    },
                });
        }

    }

    selectIntegrationRunTime(r: IntegrationRunTime): void {
        const today = new Date();
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);

        this.lastRunTimeDateInput = new Date(today);

        today.setSeconds(1);

        this.nextRunTimeDateInput = new Date(today);

        this.integrationRunTime = r;

        this.integrationRunTime.lastRunTime = new Date(this.integrationRunTime.lastRunTime);
        this.integrationRunTime.nextRunTime = new Date(this.integrationRunTime.nextRunTime);
        if (this.integrationRunTime.lastDataSent != undefined) {
            this.integrationRunTime.lastDataSent = new Date(this.integrationRunTime.lastDataSent);
        }

        this.integrationRunTimeSelected = true;
        this.addingIntegrationRunTime = false;
    }

    checkAccess(role: string[]): boolean {
        //User requires DevOps.Write or Admin

        return this.user.checkAccess(role);
    }

    addIntegrationRunTimeSwitch(): void {
        this.integrationRunTimeSelected = false;
        this.addingIntegrationRunTime = true;
        this.integrationRunTime = {
            integrationId: this.integration().integrationId,
            lastRunTime: new Date(),
            nextRunTime: new Date(),
            importTypeId: -1,
            destinationEndpointId: -1,
        };

        this.destinationsAndImportTypes = [];

        for (const integrationRunTime of this.integrationRunTimes) {
            this.destinationsAndImportTypes.push(integrationRunTime.destinationEndpointId + ' ' + integrationRunTime.importTypeId);
        }
    }

    selectDestinationEndpoint(event: any): void {
        const destinationEndpoint = event.value;
        this.integrationRunTime.destinationEndpointId = destinationEndpoint.destinationEndpointId;
        this.integrationRunTime.destinationEndpointName = destinationEndpoint.endpointName;
    }

    selectImportType(event: any): void {
        const importType = event.value;
        this.integrationRunTime.importTypeId = importType.importTypeId;
        this.integrationRunTime.importTypeDescription = importType.typeDescription;
        // console.log(importType);
    }

    setDeletingIntegrationRunTime(): void {
        this.dialog.open(this.deleteRunTimeDialog);
    }

    deleteIntegrationRunTime(): void {
        this.savingIntegrationRuntime = true;
        this.integrationRunTimeService.deleteIntegrationRunTime(this.integrationRunTime.integrationRunTimeId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: () => {
                    this.integrationRunTimes.splice(this.integrationRunTimes.indexOf(this.integrationRunTime), 1);
                    // console.log(this.integrationRunTimes);
                    if (this.integrationRunTimes.length > 0) {
                        this.selectIntegrationRunTime(this.integrationRunTimes[0]);
                    }
                    else {
                        this.integrationRunTimeSelected = false;
                    }
                    this.destinationsAndImportTypes.splice(this.destinationsAndImportTypes.indexOf(this.integrationRunTime.destinationEndpointId + ' ' + this.integrationRunTime.importTypeId), 1);
                    this.savingIntegrationRuntime = false;
                },
                error: (_error) => {
                    this.savingIntegrationRuntime = false;
                    this.toastService.toastCreate('Failed to delete the runtime for ' + this.integration().name + ': ' + this.integrationRunTime.importTypeDescription + ' ' + this.integrationRunTime.destinationEndpointName + '.', 'Warning', {
                        autoClose: false,
                    });
                },
            });
    }

    getDisplayName(endpointName: string): string {
        if (this.destinationEndpoints){
            return this.destinationEndpoints().find(d => d.endpointName == endpointName).displayName;
        }
        return '';
        
    }
}
